import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axiosInstance, { endpoints } from 'src/utils/axios';

// ----------------------------------------------------------------------

const ThemeSettingsContext = createContext(null);

export const useThemeSettings = () => {
    const context = useContext(ThemeSettingsContext);
    if (!context) {
        throw new Error('useThemeSettings must be used within ThemeSettingsProvider');
    }
    return context;
};

// ----------------------------------------------------------------------

/**
 * ThemeSettings Provider
 * Manages theme settings with:
 * - API persistence
 * - Real-time WebSocket synchronization
 * - localStorage caching for offline support
 * - Cross-platform sync (web/mobile)
 */
export function ThemeSettingsProvider({ children }) {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const updateTimeoutRef = useRef(null);

    // Default settings
    const defaultSettings = useMemo(() => ({
        mode: 'light',
        contrast: 'normal',
        direction: 'ltr',
        layout: 'vertical',
        stretch: false,
        colorPreset: 'default'
    }), []);

    // Apply theme to DOM
    const applyTheme = useCallback((themeSettings) => {
        if (!themeSettings) return;

        const root = document.documentElement;
        const body = document.body;

        // Apply mode (light/dark)
        root.setAttribute('data-theme', themeSettings.mode);
        body.classList.remove('light', 'dark');
        body.classList.add(themeSettings.mode);

        // Apply direction (ltr/rtl)
        root.setAttribute('dir', themeSettings.direction);
        body.setAttribute('dir', themeSettings.direction);

        // Apply contrast (normal/high)
        root.setAttribute('data-contrast', themeSettings.contrast);

        // Apply layout
        body.className = body.className.replace(/layout-\w+/g, '');
        body.classList.add(`layout-${themeSettings.layout}`);

        // Apply stretch
        if (themeSettings.stretch) {
            body.classList.add('stretch');
        } else {
            body.classList.remove('stretch');
        }

        // Apply color preset
        root.setAttribute('data-color-preset', themeSettings.colorPreset);

        console.log('✅ Theme applied:', themeSettings);
    }, []);

    // Fetch settings from API
    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);

            // Skip API call if user is not authenticated (no token)
            const userToken = localStorage.getItem('accessToken');
            if (!userToken) {
                // Load from cache or use defaults
                const cached = localStorage.getItem('theme_settings');
                if (cached) {
                    try {
                        const cachedSettings = JSON.parse(cached);
                        setSettings(cachedSettings);
                        applyTheme(cachedSettings);
                    } catch {
                        setSettings(defaultSettings);
                        applyTheme(defaultSettings);
                    }
                } else {
                    setSettings(defaultSettings);
                    applyTheme(defaultSettings);
                }
                setLoading(false);
                return;
            }

            const response = await axiosInstance.get(endpoints.themeSettings.get);

            if (response.data.success) {
                const fetchedSettings = response.data.data;
                setSettings(fetchedSettings);

                // Cache in localStorage
                localStorage.setItem('theme_settings', JSON.stringify(fetchedSettings));

                // Apply theme to DOM
                applyTheme(fetchedSettings);
            }
        } catch (error) {
            console.error('Failed to fetch theme settings:', error);

            // Try to load from cache
            const cached = localStorage.getItem('theme_settings');
            if (cached) {
                try {
                    const cachedSettings = JSON.parse(cached);
                    setSettings(cachedSettings);
                    applyTheme(cachedSettings);
                } catch (parseError) {
                    // Use defaults if cache is corrupted
                    setSettings(defaultSettings);
                    applyTheme(defaultSettings);
                }
            } else {
                // Use defaults
                setSettings(defaultSettings);
                applyTheme(defaultSettings);
            }
        } finally {
            setLoading(false);
        }
    }, [defaultSettings, applyTheme]);

    // Update settings (with debouncing)
    const updateSettings = useCallback(async (updates) => {
        try {
            setSyncing(true);

            // Optimistically update local state
            const newSettings = { ...settings, ...updates };
            setSettings(newSettings);
            applyTheme(newSettings);

            // Cache immediately
            localStorage.setItem('theme_settings', JSON.stringify(newSettings));

            // Debounce API call
            if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
            }

            updateTimeoutRef.current = setTimeout(async () => {
                try {
                    const response = await axiosInstance.put(
                        endpoints.themeSettings.update,
                        updates,
                        {
                            headers: {
                                'X-Platform': 'web'
                            }
                        }
                    );

                    if (response.data.success) {
                        const updatedSettings = response.data.data;
                        setSettings(updatedSettings);
                        localStorage.setItem('theme_settings', JSON.stringify(updatedSettings));
                        console.log('✅ Theme settings synced to server');
                    }
                } catch (error) {
                    console.error('Failed to sync theme settings:', error);
                    // Keep local changes even if API fails
                } finally {
                    setSyncing(false);
                }
            }, 500); // 500ms debounce

        } catch (error) {
            console.error('Failed to update theme settings:', error);
            setSyncing(false);
        }
    }, [settings, applyTheme]);

    // Reset to defaults
    const resetSettings = useCallback(async () => {
        try {
            setSyncing(true);
            const response = await axiosInstance.post(endpoints.themeSettings.reset);

            if (response.data.success) {
                const resetData = response.data.data;
                setSettings(resetData);
                applyTheme(resetData);
                localStorage.setItem('theme_settings', JSON.stringify(resetData));
                console.log('✅ Theme settings reset to defaults');
            }
        } catch (error) {
            console.error('Failed to reset theme settings:', error);
            // Fallback to local defaults
            setSettings(defaultSettings);
            applyTheme(defaultSettings);
            localStorage.setItem('theme_settings', JSON.stringify(defaultSettings));
        } finally {
            setSyncing(false);
        }
    }, [defaultSettings, applyTheme]);

    // Fetch settings on mount
    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    // Re-fetch theme settings when user logs in
    // - 'auth:login' custom event handles same-tab login (storage event doesn't fire in same tab)
    // - 'storage' event handles cross-tab login
    useEffect(() => {
        const handleLogin = () => {
            fetchSettings();
        };

        window.addEventListener('auth:login', handleLogin);
        window.addEventListener('storage', (event) => {
            if (event.key === 'accessToken' && event.newValue) {
                fetchSettings();
            }
        });

        return () => {
            window.removeEventListener('auth:login', handleLogin);
        };
    }, [fetchSettings]);

    // Apply theme whenever settings change
    useEffect(() => {
        if (settings) {
            applyTheme(settings);
        }
    }, [settings, applyTheme]);

    // Cleanup timeout on unmount
    useEffect(() => () => {
        if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
        }
    }, []);

    const value = useMemo(() => ({
        settings,
        loading,
        syncing,
        updateSettings,
        resetSettings,
        refetch: fetchSettings
    }), [settings, loading, syncing, updateSettings, resetSettings, fetchSettings]);

    return (
        <ThemeSettingsContext.Provider value={value}>
            {children}
        </ThemeSettingsContext.Provider>
    );
}

ThemeSettingsProvider.propTypes = {
    children: PropTypes.node.isRequired
};
